import { useMemo } from 'react';
import { Check, Minus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { TABLE_HEADER_CLASS } from '@/styles/dataTableBg';
import type { TeacherDetail } from '../types/teacherSubmission.types';
import { buildExpertiseGrid } from '../utils/expertiseGrid';

interface TaughtMarkProps {
  isTaught: boolean;
}

function TaughtMark({ isTaught }: TaughtMarkProps) {
  if (!isTaught) {
    return (
      <span
        role="img"
        aria-label="Does not teach"
        className="mx-auto flex size-4 items-center justify-center text-muted-foreground/40"
      >
        <Minus className="size-3" aria-hidden />
      </span>
    );
  }

  return (
    <span
      role="img"
      aria-label="Teaches"
      className="mx-auto flex size-4 items-center justify-center rounded bg-primary text-primary-foreground"
    >
      <Check className="size-3" strokeWidth={4} aria-hidden />
    </span>
  );
}

interface TeacherExpertiseGridProps {
  teacher: TeacherDetail;
}

export function TeacherExpertiseGrid({ teacher }: TeacherExpertiseGridProps) {
  const { classes, rows } = useMemo(() => buildExpertiseGrid(teacher), [teacher]);

  if (classes.length === 0 && rows.length === 0) {
    return <p className="px-4 py-3 text-sm text-muted-foreground">No classes or subjects added.</p>;
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col gap-2 px-4 py-3">
        <div className="flex flex-wrap gap-1.5">
          {classes.map((teacherClass) => (
            <Badge key={teacherClass.id} variant="secondary" className="font-normal">
              {teacherClass.name}
            </Badge>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">No subjects added.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className={TABLE_HEADER_CLASS}>
          <tr>
            <th scope="col" className="px-4 py-2 text-left text-xs-plus font-semibold">
              Subject
            </th>
            {classes.map((teacherClass) => (
              <th
                key={teacherClass.id}
                scope="col"
                title={teacherClass.name}
                className="px-3 py-2 text-center text-xs-plus font-semibold whitespace-nowrap"
              >
                {teacherClass.shortName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.subjectId} className="border-t transition-colors hover:bg-muted/30">
              <th scope="row" className="max-w-56 px-4 py-2.5 text-left font-medium">
                <span className="flex items-center gap-1.5">
                  <span className="truncate">{row.subjectName}</span>
                  {row.isMainSubject && (
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-1.5 text-3xs font-semibold',
                        ACCENT_MARK_CLASS,
                      )}
                    >
                      Main
                    </span>
                  )}
                </span>
              </th>
              {classes.map((teacherClass) => (
                <td key={teacherClass.id} className="px-3 py-2.5">
                  <TaughtMark isTaught={row.classIds.has(teacherClass.id)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface TeacherExpertiseGridSkeletonProps {
  rowCount: number;
}

export function TeacherExpertiseGridSkeleton({ rowCount }: TeacherExpertiseGridSkeletonProps) {
  return (
    <div aria-hidden>
      <div className="flex items-center justify-between gap-4 bg-muted/40 px-4 py-2.5">
        <Skeleton className="h-3 w-14" />
        <Skeleton className="h-3 w-10" />
      </div>
      {Array.from({ length: rowCount }, (_, index) => (
        <div key={index} className="flex items-center justify-between gap-4 border-t px-4 py-3">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="mr-3 size-4 rounded" />
        </div>
      ))}
    </div>
  );
}
